"use client";

import * as React from "react";

import { cn } from "lib/utils";
import { Loader2, MapPin, PencilLine } from "lucide-react";

import { useGooglePlacesAutocomplete } from "@/hooks/useGooglePlacesAutocomplete";
import type { AddressComponents, PlacePrediction } from "@/types/address";

import { Input } from "js/components/ui/input";

export interface AddressAutocompleteProps {
  /**
   * Callback when an address is selected
   */
  onAddressSelect: (address: AddressComponents) => void;
  /**
   * Callback to switch to manual entry mode
   */
  onManualModeClick?: () => void;
  /**
   * Placeholder text for the search input
   */
  placeholder?: string;
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
  /**
   * Current input value (controlled)
   */
  value?: string;
  /**
   * Callback when input value changes
   */
  onValueChange?: (value: string) => void;
  /**
   * Additional class names for the input
   */
  className?: string;
  /**
   * Show the "Enter manually" option
   */
  showManualOption?: boolean;
  /**
   * Label for the manual entry option
   */
  manualOptionLabel?: string;
  /**
   * ID for the input element
   */
  id?: string;
}

/**
 * AddressAutocomplete Component (Inline Version - Like Vercel)
 *
 * A simple inline input with dropdown suggestions.
 * - Direct input field (no popover/modal)
 * - Dropdown appears below when typing
 * - Suggestions from 2 characters
 * - Clean, minimal UX
 *
 * @example
 * <AddressAutocomplete
 *   onAddressSelect={(address) => console.log(address)}
 *   placeholder="Escribe una dirección..."
 * />
 */
export function AddressAutocomplete({
  onAddressSelect,
  onManualModeClick,
  placeholder = "Escribe una dirección...",
  disabled = false,
  value: controlledValue,
  onValueChange,
  className,
  showManualOption = true,
  manualOptionLabel = "Ingresar manualmente",
  id,
}: AddressAutocompleteProps) {
  const [internalValue, setInternalValue] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSelectingPlace, setIsSelectingPlace] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(-1);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Controlled vs uncontrolled value
  const isControlled = controlledValue !== undefined;
  const inputValue = isControlled ? controlledValue : internalValue;

  const {
    isReady,
    isLoading,
    predictions,
    error,
    search,
    getPlaceDetails,
    clearPredictions,
  } = useGooglePlacesAutocomplete();

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (isControlled) {
      onValueChange?.(newValue);
    } else {
      setInternalValue(newValue);
    }

    search(newValue);
    setIsOpen(true);
    setSelectedIndex(-1);
  };

  // Handle address selection
  const handleSelectPrediction = async (prediction: PlacePrediction) => {
    setIsSelectingPlace(true);
    setIsOpen(false);

    try {
      const details = await getPlaceDetails(prediction.place_id);
      if (details) {
        // Update input with selected address
        const displayText = prediction.description;
        if (isControlled) {
          onValueChange?.(displayText);
        } else {
          setInternalValue(displayText);
        }
        onAddressSelect(details);
      }
    } catch (err) {
      console.error("Failed to get place details:", err);
    } finally {
      setIsSelectingPlace(false);
      clearPredictions();
    }
  };

  // Handle manual mode selection
  const handleManualMode = () => {
    setIsOpen(false);
    clearPredictions();
    onManualModeClick?.();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    if (predictions.length === 0) {
      // Allow Enter to trigger manual mode when dropdown shows only "Ingresar manualmente"
      if (e.key === "Enter" && showManualOption) {
        e.preventDefault();
        handleManualMode();
      }
      return;
    }

    const maxIndex = showManualOption
      ? predictions.length
      : predictions.length - 1;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev < maxIndex ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : maxIndex));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handleSelectPrediction(predictions[selectedIndex]);
        } else if (selectedIndex === predictions.length && showManualOption) {
          handleManualMode();
        }
        break;
      case "Escape":
        setIsOpen(false);
        clearPredictions();
        break;
    }
  };

  // Handle focus
  const handleFocus = () => {
    // Show dropdown on focus so user can click "Ingresar manualmente" immediately
    if (showManualOption) {
      setIsOpen(true);
    }
  };

  // Handle blur - close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Show dropdown when predictions arrive
  React.useEffect(() => {
    if (predictions.length > 0 && inputValue.length >= 2) {
      setIsOpen(true);
    }
  }, [predictions, inputValue]);

  const showDropdown =
    isOpen &&
    (predictions.length > 0 || isLoading || error || showManualOption);

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Input Field */}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled || !isReady || isSelectingPlace}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          className={cn("pl-9 pr-8", className)}
        />
        {(isLoading || isSelectingPlace) && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 size-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Dropdown Suggestions */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg"
        >
          {/* Loading State */}
          {isLoading && predictions.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              <span>Buscando...</span>
            </div>
          )}

          {/* Error State */}
          {error && !isLoading && (
            <div className="px-3 py-3 text-sm text-red-500">{error}</div>
          )}

          {/* Predictions List */}
          {predictions.length > 0 && (
            <ul className="max-h-[280px] overflow-y-auto py-1">
              {predictions.map((prediction, index) => (
                <li
                  key={prediction.place_id}
                  onClick={() => handleSelectPrediction(prediction)}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={cn(
                    "flex cursor-pointer items-start gap-2 px-3 py-2 text-sm transition-colors",
                    selectedIndex === index
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-accent/50",
                  )}
                >
                  <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium truncate">
                      {prediction.structured_formatting.main_text}
                    </span>
                    <span className="text-xs text-muted-foreground truncate">
                      {prediction.structured_formatting.secondary_text}
                    </span>
                  </div>
                </li>
              ))}

              {/* Manual Entry Option */}
              {showManualOption && (
                <>
                  <li className="mx-2 my-1 h-px bg-border" />
                  <li
                    onClick={handleManualMode}
                    onMouseEnter={() => setSelectedIndex(predictions.length)}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-muted-foreground transition-colors",
                      selectedIndex === predictions.length
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent/50",
                    )}
                  >
                    <PencilLine className="size-4 shrink-0" />
                    <span>{manualOptionLabel}</span>
                  </li>
                </>
              )}
            </ul>
          )}

          {/* No Results - Show Manual Option */}
          {!isLoading &&
            !error &&
            predictions.length === 0 &&
            inputValue.length >= 2 && (
              <div className="py-2">
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No se encontraron resultados
                </div>
                {showManualOption && (
                  <button
                    type="button"
                    onClick={handleManualMode}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-accent/50 transition-colors"
                  >
                    <PencilLine className="size-4 shrink-0" />
                    <span>{manualOptionLabel}</span>
                  </button>
                )}
              </div>
            )}

          {/* Initial State - Show Manual Option */}
          {!isLoading &&
            !error &&
            predictions.length === 0 &&
            inputValue.length < 2 &&
            showManualOption && (
              <div className="py-2">
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  Escribe para buscar una dirección
                </div>
                <button
                  type="button"
                  onClick={handleManualMode}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:bg-accent/50 transition-colors"
                >
                  <PencilLine className="size-4 shrink-0" />
                  <span>{manualOptionLabel}</span>
                </button>
              </div>
            )}

          {/* Google Attribution */}
          <div className="border-t px-3 py-1.5 text-[10px] text-muted-foreground">
            Powered by Google
          </div>
        </div>
      )}
    </div>
  );
}
